/* eslint-disable no-undef */
class PredictiveSearch extends SearchForm {
  constructor () {
    super()
    this.cachedResults = {}
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search')
    this.isOpen = false
    this.abortController = new AbortController()
    this.searchTerm = ''

    this.setupEventListeners()
  }

  setupEventListeners () {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this))

    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
  }

  getQuery () {
    return this.input.value.trim()
  }

  onChange () {
    super.onChange()
    const newSearchTerm = this.getQuery()
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove()
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm)

    this.searchTerm = newSearchTerm

    if (!this.searchTerm.length) {
      this.close(true)
      return
    }

    this.getSearchResults(this.searchTerm)
  }

  onFormSubmit (event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault()
  }

  onFormReset (event) {
    super.onFormReset(event)
    if (super.shouldResetForm()) {
      this.searchTerm = ''
      this.abortController.abort()
      this.abortController = new AbortController()
      this.closeResults(true)
    }
  }

  onFocus () {
    const currentSearchTerm = this.getQuery()

    if (!currentSearchTerm.length) return

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange()
    } else if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      this.getSearchResults(this.searchTerm)
    }
  }

  onFocusOut () {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  onKeyup (event) {
    if (!this.getQuery().length) this.close(true)
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  onKeydown (event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
  }

  updateSearchForTerm (previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]')
    const currentButtonText = searchForTextElement?.innerText
    if (currentButtonText && previousTerm) {
      try {
        // Escape special regex characters
        const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const matches = currentButtonText.match(new RegExp(escapedTerm, 'g'))
        if (matches && matches.length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm)
        searchForTextElement.innerText = newButtonText
      } catch (e) {
        // If regex fails, just update with the new term
        searchForTextElement.innerText = `Search for "${newTerm}"`
      }
    } else if (searchForTextElement) {
      // If no previous term, just set the new term
      searchForTextElement.innerText = `Search for "${newTerm}"`
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    )
    let activeElementIndex = 0

    if (moveUp && !selectedElement) return

    let selectedElementIndex = -1
    let i = 0

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i
      }
      i++
    }

    this.statusElement.textContent = ''

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1
    }

    if (activeElementIndex === selectedElementIndex) return

    const activeElement = allVisibleElements[activeElementIndex]

    activeElement.setAttribute('aria-selected', true)
    if (selectedElement) selectedElement.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption () {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')

    if (selectedOption) selectedOption.click()
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    // Fetch both Shopify predictive search and metaobject search results in parallel
    const shopifySearch = fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          throw error
        }
        return response.text()
      })

    // Get metaobject search configuration
    const metaobjectConfig = document.querySelector('[data-metaobject-search-config]')
    let metaobjectSearch = Promise.resolve(null)

    if (metaobjectConfig) {
      const config = JSON.parse(metaobjectConfig.textContent)
      if (config.workerEndpoint && searchTerm.length >= (config.minQueryLength || 2)) {
        // Determine environment
        let environment = 'production'
        const hostname = window.location.hostname
        if (hostname.includes('staging') || hostname.includes('singular-sleep-staging') ||
            (hostname.endsWith('.myshopify.com') && !hostname.includes('sleepdoctor'))) {
          environment = 'staging'
        }

        metaobjectSearch = fetch(config.workerEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shop-Environment': environment
          },
          body: JSON.stringify({
            query: searchTerm,
            limit: 5, // Limit results for predictive search
            types: config.metaobjectTypes || []
          }),
          signal: this.abortController.signal
        })
          .then(response => {
            if (response.ok) {
              return response.json()
            }
            return null
          })
          .catch(() => null) // Fail silently for metaobject search
      }
    }

    // Wait for both searches to complete
    Promise.all([shopifySearch, metaobjectSearch])
      .then(([shopifyMarkup, metaobjectData]) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(shopifyMarkup, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML

        // Inject metaobject results if available
        const finalMarkup = this.injectMetaobjectResults(resultsMarkup, metaobjectData)

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = finalMarkup
        })
        this.renderSearchResults(finalMarkup)
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return
        }
        this.close()
        throw error
      })
  }

  setLiveRegionLoadingState () {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text')

    this.setLiveRegionText(this.loadingText)
    this.setAttribute('loading', true)
  }

  setLiveRegionText (statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults (resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)

    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults () {
    this.removeAttribute('loading')
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent)
  }

  getResultsMaxHeight () {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom
    return this.resultsMaxHeight
  }

  open () {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`
    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', true)
    this.isOpen = true
  }

  close (clearSearchTerm = false) {
    this.closeResults(clearSearchTerm)
    this.isOpen = false
  }

  closeResults (clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }
    const selected = this.querySelector('[aria-selected="true"]')

    if (selected) selected.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('loading')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style')
  }

  injectMetaobjectResults (shopifyMarkup, metaobjectData) {
    // Handle different possible response structures
    let results = null
    if (metaobjectData) {
      // Check for common response patterns
      results = metaobjectData.items || metaobjectData.results || metaobjectData.data
      // If the response is directly an array
      if (Array.isArray(metaobjectData)) {
        results = metaobjectData
      }
    }

    if (!results || !Array.isArray(results) || results.length === 0) {
      return shopifyMarkup
    }

    // Parse the Shopify markup
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = shopifyMarkup

    // Find the main predictive search results container
    let predictiveSearchResults = tempDiv.querySelector('#predictive-search-results')

    // If no predictive search results container exists, create one
    if (!predictiveSearchResults) {
      predictiveSearchResults = document.createElement('div')
      predictiveSearchResults.id = 'predictive-search-results'
      predictiveSearchResults.setAttribute('role', 'listbox')
      tempDiv.appendChild(predictiveSearchResults)
    }

    // Find or create the groups wrapper
    let resultsGroupsWrapper = predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper')

    if (!resultsGroupsWrapper) {
      resultsGroupsWrapper = document.createElement('div')
      resultsGroupsWrapper.id = 'predictive-search-results-groups-wrapper'
      resultsGroupsWrapper.className = 'predictive-search__results-groups-wrapper'
      predictiveSearchResults.appendChild(resultsGroupsWrapper)
    }

    // Check if metaobject section already exists and remove it
    const existingMetaobjectSection = resultsGroupsWrapper.querySelector('#predictive-search-metaobjects')?.closest('.predictive-search__result-group')
    if (existingMetaobjectSection) {
      existingMetaobjectSection.remove()
    }

    // Create metaobject results section
    const metaobjectSection = document.createElement('div')
    metaobjectSection.className = 'predictive-search__result-group'

    const metaobjectContainer = document.createElement('div')
    metaobjectContainer.className = 'predictive-search__pages-wrapper'

    // Add heading
    const heading = document.createElement('h2')
    heading.id = 'predictive-search-metaobjects'
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing'
    heading.textContent = 'Articles'
    metaobjectContainer.appendChild(heading)

    // Add results list
    const resultsList = document.createElement('ul')
    resultsList.id = 'predictive-search-results-metaobjects-list'
    resultsList.className = 'predictive-search__results-list list-unstyled'
    resultsList.setAttribute('role', 'group')
    resultsList.setAttribute('aria-labelledby', 'predictive-search-metaobjects')

    // Add metaobject results (limit to 5 for predictive search)
    results.slice(0, 5).forEach((result, index) => {
      const listItem = document.createElement('li')
      listItem.id = `predictive-search-option-metaobject-${index + 1}`
      listItem.className = 'predictive-search__list-item'
      listItem.setAttribute('role', 'option')
      listItem.setAttribute('aria-selected', 'false')

      const link = document.createElement('a')
      link.href = result.url || (result.handle ? `/pages/${result.handle}` : '#')
      link.className = 'predictive-search__item link link--text'
      link.setAttribute('tabindex', '-1')

      const content = document.createElement('div')
      content.className = 'predictive-search__item-content predictive-search__item-content--centered'

      const title = document.createElement('p')
      title.className = 'predictive-search__item-heading h5'
      title.textContent = result.title || result.name || 'Untitled'

      content.appendChild(title)
      link.appendChild(content)
      listItem.appendChild(link)
      resultsList.appendChild(listItem)
    })

    metaobjectContainer.appendChild(resultsList)
    metaobjectSection.appendChild(metaobjectContainer)

    // Insert metaobject section in the appropriate position
    // Check if there are existing result groups
    const existingGroups = resultsGroupsWrapper.querySelectorAll('.predictive-search__result-group')

    if (existingGroups.length > 0) {
      // If there's only one group (suggestions/collections on the left), add after it
      // If there are two groups (left and right columns), insert between them
      if (existingGroups.length === 1) {
        // Insert after the first group
        existingGroups[0].parentNode.insertBefore(metaobjectSection, existingGroups[0].nextSibling)
      } else {
        // Insert as the second group (before products)
        existingGroups[0].parentNode.insertBefore(metaobjectSection, existingGroups[1])
      }
    } else {
      // No existing groups, just append
      resultsGroupsWrapper.appendChild(metaobjectSection)
    }

    // Ensure the search button exists at the end
    const searchButton = predictiveSearchResults.querySelector('#predictive-search-option-search-keywords')
    if (!searchButton) {
      const searchButtonDiv = document.createElement('div')
      searchButtonDiv.id = 'predictive-search-option-search-keywords'
      searchButtonDiv.className = 'predictive-search__search-for-button'

      // Find the existing search button text or create new one
      const existingSearchText = tempDiv.querySelector('[data-predictive-search-search-for-text]')
      const searchText = existingSearchText ? existingSearchText.textContent : `Search for "${results.query || ''}"`

      searchButtonDiv.innerHTML = `
        <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
          <span data-predictive-search-search-for-text>${searchText}</span>
          <span class="svg-wrapper">→</span>
        </button>
      `
      predictiveSearchResults.appendChild(searchButtonDiv)
    }

    return tempDiv.innerHTML
  }
}

customElements.define('predictive-search', PredictiveSearch)
